import { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ArtworkObjectImage } from '../artwork/ArtworkObjectImage';
import { useArtworkObjectUrl } from '../../hooks/useArtworkObjectUrl';

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
`;

const ArtworkStrip = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.md} 0;
  justify-content: center;
  flex-wrap: wrap;
`;

const ArtworkThumbnail = styled(motion.div)<{ $isActive: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${({ theme, $isActive }) => 
    $isActive ? theme.colors.primary : 'transparent'};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MainArtworkContainer = styled.div`
  position: relative;
  width: 100%;
  height: 250px;
  background: ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const MainArtwork = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaceholderContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: linear-gradient(45deg, 
    ${({ theme }) => theme.colors.gray.light} 25%, 
    transparent 25%
  ),
  linear-gradient(-45deg, 
    ${({ theme }) => theme.colors.gray.light} 25%, 
    transparent 25%
  ),
  linear-gradient(45deg, 
    transparent 75%, 
    ${({ theme }) => theme.colors.gray.light} 75%
  ),
  linear-gradient(-45deg, 
    transparent 75%, 
    ${({ theme }) => theme.colors.gray.light} 75%
  );
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const PlaceholderIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  opacity: 0.5;
`;

const PlaceholderText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  opacity: 0.7;
`;

const NavigationButtons = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  width: 100%;
  pointer-events: none;
`;

const NavButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  transition: all ${({ theme }) => theme.transitions.fast};
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
`;

interface ArtworkCarouselProps {
  artworks: Array<{
    artwork_id: number;
    student_name?: string;
    uploaded_at: string;
    updated_at?: string;
  }>;
  className?: string;
}

export function ArtworkCarousel({ artworks, className }: ArtworkCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const currentArtwork = artworks?.[activeIndex];
  const currentImageUrl = useArtworkObjectUrl(
    currentArtwork?.artwork_id,
    'image',
    currentArtwork?.updated_at,
    'public',
    Boolean(currentArtwork),
  );

  if (!artworks || artworks.length === 0) {
    return (
      <CarouselContainer className={className}>
        <MainArtworkContainer>
          <PlaceholderContainer>
            <PlaceholderIcon>🎨</PlaceholderIcon>
            <PlaceholderText>No artworks available</PlaceholderText>
          </PlaceholderContainer>
        </MainArtworkContainer>
      </CarouselContainer>
    );
  }

  const goToPrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === 0 ? artworks.length - 1 : prev - 1));
  };

  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === artworks.length - 1 ? 0 : prev + 1));
  };

  return (
    <CarouselContainer className={className}>
      {/* Main artwork display */}
      <MainArtworkContainer>
        <AnimatePresence mode="wait">
          <MainArtwork
            key={currentArtwork.artwork_id}
            src={currentImageUrl || undefined}
            alt={`Artwork by ${currentArtwork.student_name || 'Unknown Student'}`}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          />
        </AnimatePresence>
        
        {/* Navigation buttons - only show if more than 1 artwork */}
        {artworks.length > 1 && (
          <NavigationButtons>
            <NavButton
              onClick={goToPrevious}
              disabled={artworks.length <= 1}
            >
              ‹
            </NavButton>
            <NavButton
              onClick={goToNext}
              disabled={artworks.length <= 1}
            >
              ›
            </NavButton>
          </NavigationButtons>
        )}
      </MainArtworkContainer>

      {/* Thumbnail strip - only show if more than 1 artwork */}
      {artworks.length > 1 && (
        <ArtworkStrip>
          {artworks.map((artwork, index) => (
            <ArtworkThumbnail
              key={artwork.artwork_id}
              $isActive={index === activeIndex}
              onClick={(e) => {
                e.stopPropagation();
                setActiveIndex(index);
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArtworkObjectImage
                artworkId={artwork.artwork_id}
                variant="thumbnail"
                updatedAt={artwork.updated_at}
                alt={`Thumbnail ${index + 1}`}
              />
            </ArtworkThumbnail>
          ))}
        </ArtworkStrip>
      )}
    </CarouselContainer>
  );
}
